/**
 * Importación (pura): fichero del banco → filas → movimientos normalizados → plan de revisión
 * (duplicados, categoría, compartido) y comprobación con extracto. Port fiel de Domain_Import.js.
 */
import { parseEsAmount, roundAmount } from './text.js';
import type { Categorized } from './rules.js';
import type { Category } from './model.js';

export interface ImportAdapter {
  label: string;
  fileTypes: string[];
  sheetName?: string;
  csvDelimiter?: string;
  columns: Partial<Record<'valueDate' | 'date' | 'concept' | 'movement' | 'amount' | 'balance' | 'notes', string>>;
  required: Array<'valueDate' | 'date' | 'concept' | 'amount'>;
}

export const IMPORT_ADAPTERS: Record<string, ImportAdapter> = {
  BBVA: {
    label: 'BBVA', fileTypes: ['xlsx'], sheetName: 'Informe BBVA',
    columns: { valueDate: 'FVALOR', date: 'FECHA', concept: 'CONCEPTO', movement: 'MOVIMIENTO', amount: 'IMPORTE', balance: 'DISPONIBLE', notes: 'OBSERVACIONES' },
    required: ['valueDate', 'date', 'concept', 'amount'],
  },
  ING: {
    label: 'ING', fileTypes: ['xls', 'xlsx'],
    columns: { valueDate: 'FVALOR', concept: 'DESCRIPCION', amount: 'IMPORTE', balance: 'SALDO' },
    required: ['valueDate', 'concept', 'amount'],
  },
  LK: {
    label: 'Laboral Kutxa', fileTypes: ['csv'], csvDelimiter: ';',
    columns: { date: 'FECHA', valueDate: 'FECHAVALOR', concept: 'CONCEPTO', amount: 'IMPORTE', balance: 'SALDOPOSTERIOR' },
    required: ['date', 'concept', 'amount'],
  },
  /** Para cuentas sin extracto (p. ej. Tickets/Pluxee, que solo enseña los movimientos en su app): un CSV a mano. */
  GENERICO: {
    label: 'Genérico (CSV: Fecha;Concepto;Importe;Saldo)', fileTypes: ['csv'], csvDelimiter: ';',
    columns: { date: 'FECHA', concept: 'CONCEPTO', amount: 'IMPORTE', balance: 'SALDO' },
    required: ['date', 'concept', 'amount'],
  },
  MYINVESTOR: {
    label: 'MyInvestor', fileTypes: ['csv'], csvDelimiter: ';',
    columns: { date: 'FECHADEOPERACION', valueDate: 'FECHADEVALOR', concept: 'CONCEPTO', amount: 'IMPORTE' },
    required: ['date', 'concept', 'amount'],
  },
};

/**
 * Lo que cada persona marca por defecto al importar en una cuenta: categorías que son compartidas y categorías que
 * además se envían a Tricount (implica compartida). Son datos de la persona, no del código: sin nada, nada se marca.
 */
export interface ImportDefaults { shared: string[]; tricount: string[] }

const catKey = (s: string) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

/** Compartido y Tricount por defecto para una categoría (sin distinguir mayúsculas ni acentos). Tricount implica compartido. */
export function importDefaultsFor(defaults: ImportDefaults | null | undefined, category: string): { shared: boolean; tricount: boolean } {
  const k = catKey(category);
  if (!defaults || !k) return { shared: false, tricount: false };
  const tricount = defaults.tricount.some(c => catKey(c) === k);
  return { shared: tricount || defaults.shared.some(c => catKey(c) === k), tricount };
}

export function normHeader(s: unknown): string {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Fecha de una celda (Date, 'dd/mm/yyyy[ hh:mm]', 'yyyy-mm-dd') → 'yyyy-MM-dd' o ''. */
export function importDate(v: unknown): string {
  const p = (n: number | string) => String(n).padStart(2, '0');
  if (v instanceof Date || Object.prototype.toString.call(v) === '[object Date]') {
    const d = v as Date;
    return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }
  const s = String(v ?? '').trim();
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) {
    const [dd, mm, yy] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const d = new Date(yy, mm - 1, dd);
    return d.getDate() === dd && d.getMonth() === mm - 1 ? `${m[3]}-${p(m[2]!)}-${p(m[1]!)}` : '';
  }
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

/** Movimiento normalizado de un fichero o del banco. */
export interface ImportRow {
  valueDate: string;
  date: string;
  description: string;
  amount: number;
  balanceAfter: number | null;
  fileIndex: number;
  sourceRef?: string;
}

export function mapImportRows(accountId: string, values: unknown[][]): { rows: ImportRow[]; warnings: string[] } {
  const ad = IMPORT_ADAPTERS[accountId];
  if (!ad) throw new Error(`No hay importador para la cuenta ${accountId}`);
  const cols = ad.columns;
  let headerRow = -1;
  let idx: Record<string, number> = {};
  for (let r = 0; r < Math.min(values.length, 30); r++) {
    const norm = (values[r] ?? []).map(normHeader);
    const found: Record<string, number> = {};
    for (const [k, h] of Object.entries(cols)) { const i = norm.indexOf(h!); if (i >= 0) found[k] = i; }
    if (ad.required.every(k => found[k] !== undefined)) { headerRow = r; idx = found; break; }
  }
  if (headerRow < 0) throw new Error(`No reconozco el fichero de ${ad.label}: no encuentro las columnas ${ad.required.map(k => cols[k]).join(', ')}`);

  const rows: ImportRow[] = [], warnings: string[] = [];
  for (let r = headerRow + 1; r < values.length; r++) {
    const row = values[r] ?? [];
    const get = (k: string): unknown => (idx[k] === undefined ? '' : row[idx[k]!]);
    if (row.every(v => v === '' || v === null || v === undefined)) continue;
    const valueDate = importDate(get('valueDate')) || importDate(get('date'));
    const date = importDate(get('date')) || valueDate;
    const rawAmount = get('amount');
    const amount = typeof rawAmount === 'number' ? rawAmount : parseEsAmount(rawAmount);
    const description = [get('concept'), get('movement'), get('notes')].map(x => String(x ?? '').trim()).filter(Boolean).join(' - ');
    const balRaw = get('balance');
    const balanceAfter = balRaw === '' || balRaw === null ? null : (typeof balRaw === 'number' ? balRaw : parseEsAmount(balRaw));
    if (!valueDate || !Number.isFinite(amount) || !description) {
      warnings.push(`Fila ${r + 1} ignorada: ${!valueDate ? 'fecha' : !Number.isFinite(amount) ? 'importe' : 'concepto'} no válido`);
      continue;
    }
    rows.push({ valueDate, date, description, amount: roundAmount(amount),
      balanceAfter: balanceAfter !== null && Number.isFinite(balanceAfter) ? roundAmount(balanceAfter) : null, fileIndex: rows.length });
  }
  return { rows, warnings };
}

/** CSV con comillas (RFC 4180) y delimitador configurable. */
export function parseCsvText(text: string, delimiter = ','): string[][] {
  const s = String(text ?? '').replace(/^﻿/, '');
  const out: string[][] = [];
  let row: string[] = [], field = '', q = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"' && s[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') q = false;
      else field += c;
    } else if (c === '"') q = true;
    else if (c === delimiter) { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++;
      row.push(field); out.push(row); row = []; field = '';
    } else field += c;
  }
  if (field !== '' || row.length) { row.push(field); out.push(row); }
  return out;
}

/** BBVA: Nómina (+) e Hipoteca (−) con fecha valor desde el día 22 cuentan en el mes siguiente (día 1). */
export function shiftValueDate(accountId: string, category: string, amount: number, valueDate: string, date: string): string {
  if (accountId !== 'BBVA') return valueDate;
  if (!((category === 'Nómina' && amount > 0) || (category === 'Hipoteca' && amount < 0))) return valueDate;
  const [fy, fm, fd] = valueDate.split('-').map(Number) as [number, number, number];
  const [y, m] = date.split('-').map(Number) as [number, number];
  if (fd < 22) return valueDate;
  const sameMonth = fy === y && fm === m;
  const nextMonth = (fy === y && m === fm + 1) || (fy === y - 1 && fm === 12 && m === 1);
  if (!sameMonth && !nextMonth) return valueDate;
  const ny = fm === 12 ? fy + 1 : fy, nm = fm === 12 ? 1 : fm + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-01`;
}

/**
 * Mes en que cuenta un movimiento que llega a final de mes: el siguiente, si su categoría lo dice («a partir del día N»).
 * Se mira la fecha valor (o la de operación) y solo si el signo es el de la categoría (ingreso > 0, gasto < 0).
 * Regla antigua (hasta que se configure en alguna categoría): BBVA, Nómina e Hipoteca, desde el día 22.
 * @return { month, from } si cuenta en otro mes que el de su fecha; null si cuenta en su mes.
 */
export function nextMonthShift(categories: Category[], category: string, amount: number, date: string, valueDate = '', legacyAccountId = ''): { month: string; from: number } | null {
  const t = nextMonthTarget(categories, category, amount, date, valueDate, legacyAccountId);
  return t && t.month !== date.slice(0, 7) ? t : null;
}

/**
 * Como nextMonthShift, pero también cuando ese mes siguiente ya es el de la fecha (la hipoteca con fecha valor el 31 de
 * octubre y fecha del 2 de noviembre): entonces la regla manda que cuente en el mes de su fecha, no en el de la fecha valor.
 */
export function nextMonthTarget(categories: Category[], category: string, amount: number, date: string, valueDate = '', legacyAccountId = ''): { month: string; from: number } | null {
  const cat = categories.find(c => c.name.toLowerCase() === category.toLowerCase());
  const configured = categories.some(c => (c.nextMonthFrom ?? 0) > 0);
  const legacy = !configured && legacyAccountId === 'BBVA' && ((category === 'Nómina' && amount > 0) || (category === 'Hipoteca' && amount < 0));
  const from = cat?.nextMonthFrom && cat.nextMonthFrom > 0 ? cat.nextMonthFrom : legacy ? 22 : 0;
  if (!from || !date) return null;
  if (cat && !legacy && ((cat.kind === 'income' && amount < 0) || (cat.kind === 'expense' && amount > 0))) return null;
  const base = valueDate || date;
  const [y, m, d] = base.split('-').map(Number) as [number, number, number];
  if (d < from) return null;
  return { month: m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`, from };
}

export const IMPORT_STOPWORDS = new Set(['PAGO', 'PAGOS', 'CON', 'TARJETA', 'TARJETAS', 'ADEUDO', 'ADEUDOS', 'RECIBO', 'RECIBOS', 'COMPRA', 'COMPRAS',
  'TRANSFERENCIA', 'TRANSFERENCIAS', 'REALIZADA', 'RECIBIDA', 'BIZUM', 'ENVIADO', 'RECIBIDO', 'OTROS', 'OTRAS',
  'DEL', 'LAS', 'LOS', 'ESP', 'CONCEPTO', 'SIN', 'DE', 'LA', 'EL', 'EN', 'ES', 'Y']);

export function importTokens(s: string): Set<string> {
  return new Set(String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()
    .split(/[^A-Z]+/).filter(w => w.length >= 2 && !IMPORT_STOPWORDS.has(w)));
}

/** Parecido (0..1): palabras significativas compartidas sobre las del concepto más corto. */
export function conceptSimilarity(a: string, b: string): number {
  const A = importTokens(a), B = importTokens(b);
  if (!A.size || !B.size) return 0;
  let shared = 0;
  A.forEach(w => { if (B.has(w)) shared++; });
  return shared / Math.min(A.size, B.size);
}

export function conceptSharedWords(a: string, b: string): number {
  const B = importTokens(b);
  return [...importTokens(a)].filter(w => w.length >= 4 && B.has(w)).length;
}

export const IMPORT_FUZZY_THRESHOLD = 0.8;
export const IMPORT_FUZZY_MARGIN = 0.3;

/** Movimiento ya registrado (claves 'yyyyMMdd', como el código actual). */
export interface ExistingForImport {
  txnId: string;
  valueDateKey: string;
  dateKey: string;
  amount: number;
  description: string;
  balanceAfter?: number | null | '';
  sourceRef?: string;
  row?: number;
  /** Origen (_Meta): 'file:…'/'sync:…' si vino del banco; '' o 'shortcut'/'app' si se apuntó a mano. */
  origin?: string;
}

/** Vino del banco (fichero o sincronización): su importe y su fecha son los del banco. */
export const fromBank = (origin: string | undefined) => !!origin && (origin.startsWith('file:') || origin.startsWith('sync:'));

/** Sugerencias (nunca automáticas): ±3 días con el mismo importe, o importe parecido de algo apuntado a mano. */
export const IMPORT_SUGGEST = { days: 3, amountTolerance: 0.15, amountMin: 1 };

export interface ImportAlias { txnId: string; date: string; amount: number; description: string }

export type ImportStatus = 'new' | 'duplicate' | 'possible-duplicate' | 'out-of-range';

export interface ImportItem {
  fileIndex: number; valueDate: string; date: string; originalValueDate: string; description: string; amount: number; balanceAfter: number | null;
  category: string; categoryIsDefault: boolean; shared: boolean; tricount: boolean;
  status: ImportStatus; fuzzy: boolean; matchTxnId: string; matchDescription: string; sourceRef: string;
  /** En los posibles duplicados: por qué se sugiere ('' mismo día e importe, 'near-date' otra fecha, 'amount' otro importe). */
  suggestion: '' | 'near-date' | 'amount'; matchDate: string; matchAmount: number | null;
  /** Posible duplicado del mismo día e importe sin ningún otro candidato (ni en el extracto ni en la app): se propone «es el mismo». */
  onlyCandidate: boolean;
}

export interface ImportPlan {
  items: ImportItem[];
  bankBalance: { amount: number; date: string } | null;
  summary: { total: number; new: number; duplicate: number; possibleDuplicate: number; outOfRange: number; fuzzyDuplicate: number; uncategorized: number };
}

interface Entry { t: ExistingForImport; used: boolean }

export function planImport(p: {
  accountId: string; rows: ImportRow[]; existing: ExistingForImport[];
  categorize: (description: string, amount: number) => Categorized | null; minDate?: string; aliases?: ImportAlias[];
  /** Compartido/Tricount por defecto de la cuenta (de la persona); sin ellos no se marca nada. */
  defaults?: ImportDefaults | null;
}): ImportPlan {
  const key = (...a: string[]) => a.join('|');
  const normC = (s: string) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim().toUpperCase().substring(0, 100);
  const iso = (k: string) => (/^\d{8}$/.test(k ?? '') ? `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}` : '');
  const amt = (n: number) => roundAmount(Number(n)).toFixed(2);

  const strong = new Map<string, Entry[]>(), legacy = new Map<string, Entry[]>(), loose = new Map<string, Entry[]>(), byRef = new Map<string, Entry[]>();
  const push = (m: Map<string, Entry[]>, k: string, e: Entry) => { const l = m.get(k); if (l) l.push(e); else m.set(k, [e]); };
  for (const t of p.existing ?? []) {
    const e: Entry = { t, used: false };
    const vd = iso(t.valueDateKey), d = iso(t.dateKey) || vd;
    if (t.balanceAfter !== null && t.balanceAfter !== undefined && t.balanceAfter !== '') push(strong, key(d, amt(t.amount), amt(Number(t.balanceAfter))), e);
    push(legacy, key(vd, d, amt(t.amount), normC(t.description)), e);
    push(loose, key(d, amt(t.amount)), e);
    if (t.sourceRef) push(byRef, String(t.sourceRef), e);
  }
  const take = (m: Map<string, Entry[]>, k: string) => { const l = m.get(k); const e = l?.find(x => !x.used); if (e) e.used = true; return e; };
  const byTxn = new Map<string, Entry>();
  strong.forEach(l => l.forEach(e => byTxn.set(e.t.txnId, e)));
  legacy.forEach(l => l.forEach(e => byTxn.set(e.t.txnId, e)));
  const alias = new Map<string, Entry[]>();
  for (const a of p.aliases ?? []) { const e = byTxn.get(String(a.txnId)); if (e) push(alias, key(a.date, amt(a.amount), normC(a.description)), e); }

  interface Prep { r: ImportRow; c: Categorized; valueDate: string; oldShifted: string; status: ImportStatus; match: Entry | null; fuzzy: boolean; suggestion: ImportItem['suggestion']; only: boolean }
  const prepared: Prep[] = p.rows.map(r => {
    const c = p.categorize(r.description, r.amount) ?? { category: '', isDefault: true };
    // La fecha valor es la del banco (el mes en que cuenta se decide al revisar, con la categoría elegida); el desplazamiento
    // antiguo sí se calcula para reconocer lo importado antes, que guardó la fecha valor desplazada
    return { r, c, valueDate: r.valueDate, oldShifted: shiftValueDate(p.accountId, c.category, r.amount, r.valueDate, r.date), status: 'new', match: null, fuzzy: false, suggestion: '', only: false };
  });

  // Pasada 1: coincidencias exactas
  for (const x of prepared) {
    const r = x.r;
    if (p.minDate && r.valueDate < p.minDate && r.date < p.minDate) { x.status = 'out-of-range'; continue; }
    let m = r.sourceRef ? take(byRef, String(r.sourceRef)) : undefined;
    if (!m && r.balanceAfter !== null) m = take(strong, key(r.date, amt(r.amount), amt(r.balanceAfter)));
    if (!m) m = take(legacy, key(x.valueDate, r.date, amt(r.amount), normC(r.description))) ?? take(legacy, key(x.oldShifted, r.date, amt(r.amount), normC(r.description)));
    if (!m) m = take(alias, key(r.date, amt(r.amount), normC(r.description)));
    if (m) { x.status = 'duplicate'; x.match = m; }
  }

  // Pasada 2: mismo día e importe, por parecido del concepto
  const groups = new Map<string, Prep[]>();
  for (const x of prepared) if (x.status === 'new') { const k = key(x.r.date, amt(x.r.amount)); const l = groups.get(k); if (l) l.push(x); else groups.set(k, [x]); }
  groups.forEach((rowsInGroup, k) => {
    const free = (loose.get(k) ?? []).filter(e => !e.used);
    if (!free.length) return;
    const pairs: Array<{ x: Prep; e: Entry; sim: number }> = [];
    rowsInGroup.forEach(x => free.forEach(e => pairs.push({ x, e, sim: conceptSimilarity(x.r.description, e.t.description) })));
    pairs.sort((a, b) => b.sim - a.sim);
    const second = (x: Prep, e: Entry) => Math.max(0, ...pairs.filter(q => q.x === x && q.e !== e && !q.e.used).map(q => q.sim));
    const oneToOne = rowsInGroup.length === 1 && free.length === 1;
    for (const q of pairs) {
      if (q.x.match || q.e.used) continue;
      if (q.sim < IMPORT_FUZZY_THRESHOLD && !(oneToOne && conceptSharedWords(q.x.r.description, q.e.t.description) >= 1)) continue;
      const alone = free.length === 1;
      if (!alone && q.sim - second(q.x, q.e) < IMPORT_FUZZY_MARGIN) continue;
      q.e.used = true;
      Object.assign(q.x, { match: q.e, fuzzy: true, status: 'duplicate' });
    }
    const leftRows = rowsInGroup.filter(x => !x.match), leftFree = free.filter(e => !e.used);
    if (rowsInGroup.length > 1 && leftRows.length === 1 && leftFree.length === 1) {
      leftFree[0]!.used = true;
      Object.assign(leftRows[0]!, { match: leftFree[0], fuzzy: true, status: 'duplicate' });
    }
    for (const x of rowsInGroup.filter(y => !y.match)) {
      const best = pairs.filter(q => q.x === x && !q.e.used)[0];
      if (!best) continue;
      best.e.used = true;
      Object.assign(x, { match: best.e, status: 'possible-duplicate', only: oneToOne });
    }
  });

  // Pasada 3 (solo sugerencias, nunca coincidencias): mismo importe a ±3 días; o, si se apuntó a mano, importe parecido
  const all: Entry[] = [...new Set([...loose.values()].flat())];
  const dayOf = (e: Entry) => iso(e.t.dateKey) || iso(e.t.valueDateKey);
  const days = (a: string, b: string) => Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86400000;
  for (const x of prepared.filter(y => y.status === 'new')) {
    const near = all.filter(e => !e.used && Math.sign(e.t.amount) === Math.sign(x.r.amount) && days(dayOf(e), x.r.date) <= IMPORT_SUGGEST.days);
    const rank = (a: Entry, b: Entry) => days(dayOf(a), x.r.date) - days(dayOf(b), x.r.date)
      || conceptSimilarity(x.r.description, b.t.description) - conceptSimilarity(x.r.description, a.t.description);
    const same = near.filter(e => amt(e.t.amount) === amt(x.r.amount)).sort(rank)[0];
    const close = same ? undefined : near.filter(e => !fromBank(e.t.origin)
      && Math.abs(e.t.amount - x.r.amount) <= Math.max(IMPORT_SUGGEST.amountMin, Math.abs(x.r.amount) * IMPORT_SUGGEST.amountTolerance)
      && (conceptSimilarity(x.r.description, e.t.description) >= 0.5 || conceptSharedWords(x.r.description, e.t.description) >= 1)).sort(rank)[0];
    const e = same ?? close;
    if (!e) continue;
    e.used = true;
    Object.assign(x, { match: e, status: 'possible-duplicate', suggestion: same ? 'near-date' : 'amount' });
  }

  const items: ImportItem[] = prepared.map(x => {
    const { shared, tricount } = importDefaultsFor(p.defaults, x.c.category);
    return {
      fileIndex: x.r.fileIndex, valueDate: x.valueDate, date: x.r.date, originalValueDate: x.valueDate !== x.r.valueDate ? x.r.valueDate : '',
      description: x.r.description, amount: x.r.amount, balanceAfter: x.r.balanceAfter,
      category: x.c.category, categoryIsDefault: !!x.c.isDefault, shared, tricount,
      status: x.status, fuzzy: x.fuzzy, matchTxnId: x.match ? x.match.t.txnId : '', matchDescription: x.match ? x.match.t.description : '',
      sourceRef: x.r.sourceRef ?? '',
      suggestion: x.suggestion, matchDate: x.match ? dayOf(x.match) : '', matchAmount: x.match ? x.match.t.amount : null, onlyCandidate: x.only,
    };
  });

  let bankBalance: ImportPlan['bankBalance'] = null;
  const withBal = p.rows.filter(r => r.balanceAfter !== null);
  if (withBal.length) {
    const maxDate = withBal.reduce((m, r) => (r.date > m ? r.date : m), '');
    const sameDay = withBal.filter(r => r.date === maxDate);
    let up = 0, down = 0;
    for (let i = 1; i < withBal.length; i++) { if (withBal[i]!.date > withBal[i - 1]!.date) up++; else if (withBal[i]!.date < withBal[i - 1]!.date) down++; }
    const latest = up >= down ? sameDay[sameDay.length - 1]! : sameDay[0]!;
    bankBalance = { amount: latest.balanceAfter!, date: latest.date };
  }
  const count = (s: ImportStatus) => items.filter(i => i.status === s).length;
  return {
    items, bankBalance,
    summary: {
      total: items.length, new: count('new'), duplicate: count('duplicate'), possibleDuplicate: count('possible-duplicate'),
      outOfRange: count('out-of-range'), fuzzyDuplicate: items.filter(i => i.fuzzy).length,
      uncategorized: items.filter(i => i.status === 'new' && i.categoryIsDefault).length,
    },
  };
}
